import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";

/**
 * Hook to fetch plugin config category by key
 * Used to check if a plugin is installed and get its settings
 * @param {string} categoryKey - The config category key (e.g., 'plugin_deliverect_integration')
 * @param {Object} options - Query options
 * @returns {Object} Query result with category data
 */
export const usePluginConfigCategory = (categoryKey, options = {}) => {
  const queryParams = {
    where: JSON.stringify([{ attribute: "key", value: categoryKey }]),
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["pluginConfigCategory", categoryKey],
    queryFn: async () => {
      const response = await apiClient.get(
        `/config_categories${queryString ? `?${queryString}` : ""}`,
      );
      // Return the first result (should be unique by key)
      return response?.result?.[0] || null;
    },
    enabled: !!categoryKey && options.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
    ...options,
  });
};

/**
 * Hook to get the query client for manual invalidation
 * @returns {Object} Object with refetch function
 */
export const usePluginConfigRefetch = () => {
  const queryClient = useQueryClient();

  const refetchPluginConfig = (categoryKey) => {
    queryClient.invalidateQueries({
      queryKey: ["pluginConfigCategory", categoryKey],
    });
  };

  return { refetchPluginConfig };
};

export default {
  usePluginConfigCategory,
  usePluginConfigRefetch,
};
